import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Empty } from '../ui'

interface Props {
  /** Named in the console line, so a report says which screen threw. */
  where: string
  /** A change clears a caught error — an EventSheet passes the open row's id,
   *  so opening another event is a fresh try. */
  resetKey?: unknown
  /** Offer a reload instead of Retry. For the root only: when the shell
   *  itself threw, putting it back would throw again. */
  reload?: boolean
  /** Replaces the default box. Given the retry. */
  fallback?: (retry: () => void) => ReactNode
  children?: ReactNode
}

interface State { error: Error | null }

/**
 * A render that throws, contained to the screen or sheet it threw in
 * (UI-audit a4-04). There was none anywhere, so one bad row — E5's stale
 * cached row with no `people` — unmounted the root and left a white page on
 * every reload while the hash kept the state that triggered it.
 *
 * Cleared three ways: Retry, a change of `resetKey`, and any `hashchange` —
 * "keyed on the hash" without being a React `key`, which would remount the
 * screen and lose the state X7 keeps every tab mounted for. Navigating away
 * and back is a fresh try; a row that still throws is caught again.
 */
export class Boundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`[boundary:${this.props.where}]`, error, info.componentStack)
  }

  componentDidMount() { window.addEventListener('hashchange', this.retry) }
  componentWillUnmount() { window.removeEventListener('hashchange', this.retry) }

  componentDidUpdate(prev: Props) {
    if (this.state.error && !Object.is(prev.resetKey, this.props.resetKey)) this.retry()
  }

  retry = () => { if (this.state.error) this.setState({ error: null }) }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.retry)
    const box = (
      <Empty title="Something went wrong showing this." role="alert">
        {import.meta.env.DEV && <p>{error.message}</p>}
        <p>{this.props.reload ? 'Reloading starts it again.' : 'The rest of the app still works.'}</p>
        {this.props.reload
          ? <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>Reload</Button>
          : <Button variant="secondary" size="sm" onClick={this.retry}>Retry</Button>}
      </Empty>
    )
    return this.props.reload ? <div className="gate"><div className="box">{box}</div></div> : box
  }
}
